import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Download, Send, Loader2 } from 'lucide-react';
import Dialog from '../ui/Dialog';
import StateBlock from '../ui/StateBlock';
import { api, type ClientTaskReportInput } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import { israelToday } from '../../utils/israelTime';
import { usePermissions } from '../../hooks/usePermissions';
import { PERIOD_PRESETS, hm, mayProduceClientReport, parseEmails, periodError, presetRange, toggleExcluded, type PeriodPreset } from './clientTaskReportModel';

// ============================================================================
// FIX-CLIENT-TASK-REPORT (D-359 s.4-s.5) - "דוח משימות ללקוח": the button and its dialog, on the engagement
// page, the customer card (one engagement or "כל ההתקשרויות") and "פירוט לוגים". A period preset or two dates,
// a preview with tasks to leave out, then "הורדה" (the PDF) or "שליחה ללקוח" (kept in the portal, optionally
// e-mailed to chosen recipients). The server decides who may (s.6); a 403 is shown as it is.
// ============================================================================

export interface ClientTaskReportButtonProps {
  /** Fixed customer (engagement page / customer card). Without it the dialog offers `organizations`. */
  organizationId?: string | null;
  /** Fixed engagement (the engagement page). */
  engagementId?: string | null;
  /** Customers to pick from ("פירוט לוגים"). */
  organizations?: Array<{ id: string; label: string }>;
  /** The first choice of the pickers (the screen's own filters); the person can change it. */
  initialOrganizationId?: string | null;
  initialEngagementId?: string | null;
  className?: string;
}

export default function ClientTaskReportButton(props: ClientTaskReportButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { capabilities, openedByOverride, seesAllEmployees } = usePermissions();
  if (!mayProduceClientReport(capabilities, props.organizationId, openedByOverride('logDetail') || seesAllEmployees('logDetail'))) return null;
  return (
    <>
      <button type="button" className={props.className ?? 'btn btn-secondary'} onClick={() => setOpen(true)} data-testid="client-task-report-open">
        <FileText className="w-4 h-4" aria-hidden="true" />
        <span>{t('clientTaskReport.button')}</span>
      </button>
      {open && <ClientTaskReportDialog {...props} onClose={() => setOpen(false)} />}
    </>
  );
}

function ClientTaskReportDialog({ organizationId, engagementId, organizations, initialOrganizationId, initialEngagementId, onClose }: ClientTaskReportButtonProps & { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const today = israelToday();
  const [orgId, setOrgId] = useState<string>(organizationId ?? initialOrganizationId ?? (organizations?.length === 1 ? organizations[0].id : ''));
  const [engId, setEngId] = useState<string>(engagementId ?? (initialOrganizationId ? initialEngagementId ?? '' : ''));
  const [preset, setPreset] = useState<PeriodPreset>('sinceLast');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [email, setEmail] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [extra, setExtra] = useState('');
  const [cc, setCc] = useState('');
  const [busy, setBusy] = useState<'pdf' | 'send' | null>(null);

  const options = useQuery({
    queryKey: ['client-task-report', 'options', orgId, engId],
    queryFn: () => api.getClientTaskReportOptions({ organization_id: orgId, engagement_id: engId || null }),
    enabled: !!orgId,
    retry: false,
  });
  const opt = options.data?.data;

  // the default period: from the day after the last sent report (s.4), and the primary contact as the recipient
  useEffect(() => {
    if (!opt) return;
    if (preset === 'sinceLast') { setFrom(opt.default_period.from); setTo(opt.default_period.to); }
    setChosen((c) => (c.length ? c : opt.default_recipient ? [opt.default_recipient] : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opt?.default_period.from, opt?.default_period.to, opt?.default_recipient]);

  const pickPreset = (p: PeriodPreset) => {
    setPreset(p);
    const r = presetRange(p, today, opt?.default_period.from);
    if (r) { setFrom(r.from); setTo(r.to); }
  };

  const perr = periodError(from, to);
  const input: ClientTaskReportInput = { organization_id: orgId, engagement_id: engId || null, from, to, exclude_ticket_ids: excluded };
  const preview = useQuery({
    queryKey: ['client-task-report', 'preview', orgId, engId, from, to, [...excluded].sort().join(',')],
    queryFn: () => api.previewClientTaskReport(input),
    enabled: !!orgId && !perr,
    retry: false,
  });
  const pv = preview.data?.data;
  const history = useQuery({
    queryKey: ['client-task-report', 'sent', orgId, engId],
    queryFn: () => api.listClientTaskReports({ organization_id: orgId, engagement_id: engId || null }),
    enabled: !!orgId,
    retry: false,
  });

  const extraEmails = useMemo(() => parseEmails(extra), [extra]);
  const ccEmails = useMemo(() => parseEmails(cc), [cc]);
  const recipients = [...new Set([...chosen, ...extraEmails.valid])];
  const emailInvalid = extraEmails.invalid.length > 0 || ccEmails.invalid.length > 0;
  const canAct = !!pv && pv.rows.length > 0 && !perr && !busy;
  const canSend = canAct && (!email || (recipients.length > 0 && !emailInvalid));

  const download = async () => {
    setBusy('pdf');
    try {
      const { blob, fileName } = await api.downloadClientTaskReport(input);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? `client-task-report-${from}-${to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast('error', apiErrorMessage(e, t('clientTaskReport.pdfError')));
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    setBusy('send');
    try {
      const res = await api.sendClientTaskReport({ ...input, email, email_to: email ? recipients : [], email_cc: email ? ccEmails.valid : [] });
      const sent = res.data;
      if (sent?.email_status === 'failed') toast('error', t('clientTaskReport.sentMailFailed'));
      else toast('success', email ? t('clientTaskReport.sentWithMail', { to: recipients.join(', ') }) : t('clientTaskReport.sentPortal'));
      await qc.invalidateQueries({ queryKey: ['client-task-report'] });
      onClose();
    } catch (e) {
      toast('error', apiErrorMessage(e, t('clientTaskReport.sendError')));
    } finally {
      setBusy(null);
    }
  };

  const footer = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button type="button" className="btn btn-secondary" disabled={!canAct} onClick={() => void download()} data-testid="client-task-report-download">
        {busy === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
        <span>{t('clientTaskReport.download')}</span>
      </button>
      <button type="button" className="btn btn-primary" disabled={!canSend} onClick={() => void send()} data-testid="client-task-report-send">
        {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Send className="w-4 h-4" aria-hidden="true" />}
        <span>{t('clientTaskReport.send')}</span>
      </button>
    </div>
  );

  const forbidden = (options.error as { response?: { status?: number } } | null)?.response?.status === 403
    || (preview.error as { response?: { status?: number } } | null)?.response?.status === 403;

  return (
    <Dialog open onClose={onClose} title={t('clientTaskReport.title')} description={t('clientTaskReport.desc')} size="xl" footer={footer} panelProps={{ 'data-testid': 'client-task-report-dialog' }}>
      <div className="space-y-5">
        {/* who */}
        <div className="flex flex-wrap items-end gap-3">
          {!organizationId && (
            <label className="flex flex-col gap-1 text-sm text-text-2">
              <span>{t('clientTaskReport.customer')}</span>
              <select className="input py-1.5" value={orgId} onChange={(e) => { setOrgId(e.target.value); setEngId(''); setExcluded([]); setChosen([]); }} data-testid="client-task-report-org">
                <option value="">{t('clientTaskReport.chooseCustomer')}</option>
                {(organizations ?? []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </label>
          )}
          {orgId && !engagementId && (
            <label className="flex flex-col gap-1 text-sm text-text-2">
              <span>{t('clientTaskReport.engagement')}</span>
              <select className="input py-1.5" value={engId} onChange={(e) => { setEngId(e.target.value); setExcluded([]); }} data-testid="client-task-report-engagement">
                <option value="">{t('clientTaskReport.allEngagements')}</option>
                {(opt?.engagements ?? []).map((e) => <option key={e.id} value={e.id}>{e.name ?? e.id}</option>)}
              </select>
            </label>
          )}
        </div>

        {!orgId ? <p className="text-sm text-text-3">{t('clientTaskReport.chooseCustomerHint')}</p> : forbidden ? (
          <StateBlock kind="error" title={t('clientTaskReport.noAccess')} />
        ) : (
          <>
            {/* the period */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-text-1">{t('clientTaskReport.period')}</legend>
              <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('clientTaskReport.period')}>
                {PERIOD_PRESETS.map((p) => (
                  <button key={p} type="button" role="radio" aria-checked={preset === p} className={`btn btn-sm ${preset === p ? 'btn-primary' : 'btn-ghost'}`} onClick={() => pickPreset(p)} data-testid={`client-task-report-preset-${p}`}>
                    {t(`clientTaskReport.presets.${p}`)}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-text-2">
                  <span>{t('clientTaskReport.from')}</span>
                  <input type="date" className="input py-1.5 w-auto" value={from} max={to || undefined} onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }} data-testid="client-task-report-from" />
                </label>
                <label className="flex items-center gap-2 text-sm text-text-2">
                  <span>{t('clientTaskReport.to')}</span>
                  <input type="date" className="input py-1.5 w-auto" value={to} min={from || undefined} onChange={(e) => { setPreset('custom'); setTo(e.target.value); }} data-testid="client-task-report-to" />
                </label>
                {opt?.default_period.last_sent_end && (
                  <span className="text-xs text-text-3">{t('clientTaskReport.lastSent', { date: opt.default_period.last_sent_end.split('-').reverse().join('.') })}</span>
                )}
              </div>
              {perr === 'order' && <p role="alert" className="text-sm text-danger-700">{t('clientTaskReport.periodOrder')}</p>}
            </fieldset>

            {/* the preview */}
            {preview.isLoading ? <StateBlock kind="loading" /> : preview.isError ? (
              <StateBlock kind="error" error={preview.error} title={t('clientTaskReport.loadError')} onRetry={() => preview.refetch()} />
            ) : pv ? (
              <div className="space-y-4" data-testid="client-task-report-preview">
                <p className="text-sm text-text-2">{pv.subtitle}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Card label={t('clientTaskReport.cardPeriod')} value={pv.period_text} />
                  <Card label={t('clientTaskReport.cardTasksDays')} value={`${pv.task_count} · ${pv.work_days}`} />
                  <Card label={t('clientTaskReport.cardTotal')} value={hm(pv.total_minutes)} accent ltr />
                  {pv.extra_card && <Card label={pv.extra_card.label} value={pv.extra_card.parts.join(' · ')} />}
                </div>

                {pv.all_tasks.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-text-1 mb-1">{t('clientTaskReport.tasksTitle')}</h3>
                    <p className="text-xs text-text-3 mb-2">{t('clientTaskReport.tasksHint')}</p>
                    <ul className="divide-y divide-surface-200 rounded-lg border border-surface-200">
                      {pv.all_tasks.map((x) => (
                        <li key={x.key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!x.excluded} onChange={() => setExcluded((ex) => toggleExcluded(ex, x.ticket_ids))} data-testid="client-task-report-task" />
                            <span className={x.excluded ? 'text-text-3 line-through' : ''}>{x.task}</span>
                          </label>
                          <span className="tabular-nums text-text-2" dir="ltr">{hm(x.minutes)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {pv.rows.length === 0 ? <p className="text-sm text-text-3">{t('clientTaskReport.empty')}</p> : (
                  <div className="overflow-x-auto rounded-lg border border-surface-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-surface-800 text-white">
                        <tr>
                          <th className="px-2 py-1.5 text-start font-normal">#</th>
                          <th className="px-2 py-1.5 text-start font-normal">{t('clientTaskReport.colDate')}</th>
                          <th className="px-2 py-1.5 text-start font-normal">{t('clientTaskReport.colTask')}</th>
                          <th className="px-2 py-1.5 text-start font-normal">{t('clientTaskReport.colDesc')}</th>
                          <th className="px-2 py-1.5 text-end font-normal">{t('clientTaskReport.colDuration')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pv.rows.map((r) => (
                          <tr key={r.n} className="border-t border-surface-200 align-top even:bg-surface-50">
                            <td className="px-2 py-1.5 text-text-3">{r.n}</td>
                            <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{r.date}</td>
                            <td className="px-2 py-1.5 font-medium">{r.task}</td>
                            <td className="px-2 py-1.5 text-text-2">{r.description.map((d, i) => <div key={i}>{d}</div>)}</td>
                            <td className="px-2 py-1.5 text-end tabular-nums" dir="ltr">{hm(r.minutes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}

            {/* the send */}
            <fieldset className="space-y-2 rounded-lg border border-surface-200 p-3">
              <legend className="px-1 text-sm font-medium text-text-1">{t('clientTaskReport.sendTitle')}</legend>
              <p className="text-xs text-text-3">{t('clientTaskReport.portalNote')}</p>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} data-testid="client-task-report-email" />
                <span>{t('clientTaskReport.alsoEmail')}</span>
              </label>
              {email && (
                <div className="space-y-2 ps-6">
                  {(opt?.recipients ?? []).length === 0 && <p className="text-xs text-text-3">{t('clientTaskReport.noRecipients')}</p>}
                  {(opt?.recipients ?? []).map((r) => (
                    <label key={r.email} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={chosen.includes(r.email)} onChange={() => setChosen((c) => (c.includes(r.email) ? c.filter((x) => x !== r.email) : [...c, r.email]))} data-testid="client-task-report-recipient" />
                      <span>{r.name ? `${r.name} · ` : ''}<span dir="ltr">{r.email}</span>{r.is_primary ? ` (${t('clientTaskReport.primary')})` : ''}</span>
                    </label>
                  ))}
                  <label className="flex flex-col gap-1 text-sm text-text-2">
                    <span>{t('clientTaskReport.extraEmails')}</span>
                    <input className="input py-1.5" dir="ltr" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="name@example.com" data-testid="client-task-report-extra" />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-text-2">
                    <span>{t('clientTaskReport.cc')}</span>
                    <input className="input py-1.5" dir="ltr" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="client-task-report-cc" />
                  </label>
                  {emailInvalid && <p role="alert" className="text-sm text-danger-700">{t('clientTaskReport.badEmail', { list: [...extraEmails.invalid, ...ccEmails.invalid].join(', ') })}</p>}
                  {recipients.length === 0 && <p className="text-xs text-warning-800">{t('clientTaskReport.pickRecipient')}</p>}
                </div>
              )}
            </fieldset>

            {/* the reports already sent */}
            {(history.data?.data ?? []).length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-text-1 mb-1">{t('clientTaskReport.historyTitle')}</h3>
                <ul className="space-y-1 text-sm text-text-2" data-testid="client-task-report-history">
                  {(history.data?.data ?? []).slice(0, 10).map((h) => (
                    <li key={h.id}>
                      {t('clientTaskReport.historyLine', {
                        from: h.period_start.split('-').reverse().join('.'),
                        to: h.period_end.split('-').reverse().join('.'),
                        sent: new Date(h.sent_at).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' }),
                      })}
                      {' · '}
                      {h.email_to.length ? <span dir="ltr">{h.email_to.join(', ')}</span> : t('clientTaskReport.portalOnly')}
                      {h.email_status === 'failed' && <span className="text-danger-700"> · {t('clientTaskReport.mailFailed')}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

function Card({ label, value, accent, ltr }: { label: string; value: string; accent?: boolean; ltr?: boolean }) {
  return (
    <div className="rounded-lg bg-primary-50 px-3 py-2">
      <div className="text-xs text-text-3">{label}</div>
      <div className={`font-semibold whitespace-nowrap ${accent ? 'text-primary-600 text-lg' : 'text-text-1'}`} dir={ltr ? 'ltr' : undefined} style={ltr ? { textAlign: 'end' } : undefined}>{value}</div>
    </div>
  );
}
